import React, { useState } from "react";
import { Alert, Avatar, Box, Button, Chip, Paper, Snackbar, Stack, Typography } from "@mui/material";
import InviteDialog from "../../components/common/InviteDialog";
import ChatThreadsPanel from "./components/ChatThreadsPanel";
import ConversationPanel from "./components/ConversationPanel";
import NetworkHeaderRow from "./components/NetworkHeaderRow";
import NetworkHealthCard from "./components/NetworkHealthCard";
import NetworkInviteCard from "./components/NetworkInviteCard";
import NetworkMomentList from "./components/NetworkMomentList";
import NetworkPeopleStrip from "./components/NetworkPeopleStrip";
import NetworkSummaryCard from "./components/NetworkSummaryCard";
import { useNetworkPage } from "./hooks/useNetworkPage.js";

function NetworkLeaderboardCard({ leaderboard }) {
  const tabs = leaderboard?.tabs || [];
  const [activeKey, setActiveKey] = useState(leaderboard?.default_tab || tabs[0]?.key || "support");
  if (!tabs.length) return null;
  const active = tabs.find((tab) => tab.key === activeKey) || tabs[0];
  const rows = active?.top || [];
  const current = active?.current_user || null;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "8px",
        border: "1px solid rgba(20, 83, 45, 0.16)",
        bgcolor: "#f7fbf8",
      }}
    >
      <Stack spacing={1.4}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 900, color: "#13233d" }}>
            {leaderboard?.title || "Your people are showing up."}
          </Typography>
          <Typography sx={{ mt: 0.4, color: "#56667a", fontSize: 14, lineHeight: 1.55 }}>
            {leaderboard?.subtitle || "Support, progress, and small wins show up here."}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              size="small"
              variant={tab.key === active.key ? "contained" : "outlined"}
              onClick={() => setActiveKey(tab.key)}
              sx={{ borderRadius: "8px", fontWeight: 850 }}
            >
              {tab.label}
            </Button>
          ))}
        </Stack>
        <Box>
          <Typography sx={{ fontWeight: 900, color: "#13233d" }}>
            {active?.title || active?.label}
          </Typography>
          {active?.body ? (
            <Typography sx={{ mt: 0.2, color: "#66758b", fontSize: 13 }}>
              {active.body}
            </Typography>
          ) : null}
        </Box>
        <Stack spacing={1}>
          {rows.length ? rows.map((row) => (
            <LeaderboardRow key={`${active.key}-${row.user_id}-${row.rank}`} row={row} />
          )) : (
            <Typography sx={{ p: 1.4, borderRadius: "14px", bgcolor: "#fff", color: "#66758b" }}>
              No activity here yet this week.
            </Typography>
          )}
        </Stack>
        {current ? (
          <Box>
            <Typography sx={{ mb: 0.8, fontSize: 12, fontWeight: 900, color: "#166534", textTransform: "uppercase", letterSpacing: 0.5 }}>
              You this week
            </Typography>
            <LeaderboardRow row={current} compact />
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

function LeaderboardRow({ row, compact = false }) {
  return (
    <Stack
      direction="row"
      spacing={1.1}
      alignItems="center"
      sx={{
        p: 1.2,
        borderRadius: "8px",
        border: "1px solid rgba(23, 52, 93, 0.09)",
        bgcolor: compact ? "#fafffb" : "#fff",
      }}
    >
      <Typography sx={{ width: 38, fontSize: 13, fontWeight: 900, color: "#0f4c81" }}>
        {row?.rank ? `#${row.rank}` : "You"}
      </Typography>
      <Avatar src={row?.avatar_url || ""} sx={{ width: 36, height: 36 }}>
        {(row?.display_name || "T").slice(0, 1).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#13233d" }}>
          {row?.display_name || "Someone"}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#56667a", lineHeight: 1.45 }}>
          {row?.summary || "Showing up this week."}
        </Typography>
      </Box>
      {row?.stat_label ? <Chip size="small" label={row.stat_label} sx={{ fontWeight: 850 }} /> : null}
    </Stack>
  );
}

function NeedsAttentionCard({ summary, onOpenConnections, onOpenThread }) {
  if (!summary) return null;
  const items = [];
  const active = summary.active_sprint || null;
  const needs = (summary.needs_you || summary.needs_encouragement || [])[0] || null;

  if (summary.pending_requests_count) {
    items.push({
      id: "requests",
      title: "You have a pending request.",
      body: "Review the request from Connections.",
      cta: "Review request",
      onClick: onOpenConnections,
    });
  }
  if (needs) {
    items.push({
      id: `needs-${needs.connection_id || needs.user_id || "person"}`,
      title: `${needs.display_name || "Your TrustBud"} may need a check-in.`,
      body: needs.support_text || needs.status_text || "Send a quick check-in.",
      cta: "Send check-in",
      onClick: () => (needs.connection_id ? onOpenThread(needs.connection_id) : onOpenConnections()),
    });
  }
  if (active) {
    items.push({
      id: "active-accountability",
      title: active.partner?.display_name ? `${active.partner.display_name} is active today.` : "Your TrustBud may need you.",
      body: active.message || "Open Accountability or send a response.",
      cta: active.primary_cta?.action === "open_chat" ? active.primary_cta.label : "Open chat",
      onClick: () => {
        if (active.connection_id) {
          onOpenThread(active.connection_id);
          return;
        }
        onOpenConnections();
      },
    });
  }

  if (!items.length && !summary.headline?.title) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        maxWidth: 1120,
        borderRadius: "8px",
        border: "1px solid rgba(188, 214, 235, 0.95)",
        bgcolor: "#f5faff",
      }}
    >
      <Stack spacing={1.3}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#13233d" }}>
            Needs attention
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: 13, color: "#56667a" }}>
            Start here before scrolling.
          </Typography>
        </Box>
        {summary.headline?.title ? (
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#13233d" }}>
              {summary.headline.title}
            </Typography>
            {summary.headline.subtitle ? (
              <Typography sx={{ mt: 0.25, fontSize: 13, color: "#56667a" }}>
                {summary.headline.subtitle}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {items.slice(0, 3).map((item) => (
          <Stack
            key={item.id}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{
              p: 1.2,
              borderRadius: "8px",
              bgcolor: "#fff",
              border: "1px solid rgba(23, 52, 93, 0.08)",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#13233d" }}>
                {item.title}
              </Typography>
              <Typography sx={{ mt: 0.2, fontSize: 13, color: "#56667a" }}>
                {item.body}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={item.onClick}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 850, flexShrink: 0 }}
            >
              {item.cta}
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

export default function NetworkPage() {
  const model = useNetworkPage();
  const showHealthFallback = Boolean(model.networkHealth && !model.networkSummary);
  const leaderboard = model.networkSummary?.leaderboard?.tabs?.length
    ? model.networkSummary.leaderboard
    : null;

  React.useEffect(() => {
    window.dispatchEvent(new Event("trustbud:activity-seen"));
  }, []);

  return (
    <>
      <Box sx={{ maxWidth: 1120 }}>
        <NetworkHeaderRow activeTab={model.activeTab} onTabChange={model.setTab} />
      </Box>

      <NeedsAttentionCard
        summary={model.networkSummary}
        onOpenConnections={model.openConnections}
        onOpenThread={model.openThread}
      />

      {showHealthFallback ? (
        <Box sx={{ maxWidth: 1120, mb: 2 }}>
          <NetworkHealthCard
            nudge={model.networkHealth}
            onPrimaryAction={model.messageSomeone}
            onSecondaryAction={model.openFindPeople}
            onDismiss={model.dismissNetworkHealth}
          />
        </Box>
      ) : null}

      {model.activeTab === "messages" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(320px, 360px) minmax(0, 1fr)" },
            gap: { xs: 2, lg: 1.75 },
            alignItems: "start",
            maxWidth: 1120,
          }}
        >
          {model.moduleErrors.chats ? (
            <Alert severity="warning" sx={{ gridColumn: "1 / -1" }}>
              {model.moduleErrors.chats}
            </Alert>
          ) : null}
          <ChatThreadsPanel
            activeSprintThread={model.activeSprintThread}
            recentThreads={model.recentThreads}
            loading={model.threadsLoading}
            selectedConnectionId={model.selectedConnectionId}
            onOpenThread={(thread) => model.openThread(thread?.connectionId)}
            onOpenPerson={model.openPersonProfile}
            onOpenConnections={model.openConnections}
          />
          <ConversationPanel
            thread={model.selectedThread}
            loading={model.threadLoading}
            composerValue={model.composerValue}
            onComposerChange={model.setComposerValue}
            onSend={model.sendMessage}
            composerError={model.composerError}
            encouragementOptions={model.encouragementOptions}
            encouragementLoading={model.encouragementLoading}
            onLoadEncouragement={model.loadEncouragementOptions}
            onUseEncouragement={model.useEncouragementOption}
            sending={model.sending}
            onOpenSprint={model.openSprint}
            onStartSprint={model.startSprint}
            startingSprint={model.startingSprint}
            onOpenPerson={model.openPersonProfile}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.78fr) 276px" },
            gap: { xs: 2, lg: 1.75 },
            alignItems: "start",
            maxWidth: 1068,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack spacing={1.55}>
              {model.moduleErrors.connections ? (
                <Alert severity="warning">{model.moduleErrors.connections}</Alert>
              ) : null}
              {model.moduleErrors.moments ? (
                <Alert severity="warning">{model.moduleErrors.moments}</Alert>
              ) : null}
              <NetworkMomentList
                moments={model.moments}
                loading={model.momentsLoading}
                onOpen={model.handleMomentOpen}
                onReact={model.handleMomentReaction}
                onEmptyAction={model.openFindPeople}
              />
              <NetworkPeopleStrip
                people={model.people}
                onPersonClick={model.handlePersonClick}
                onInviteClick={model.openInviteDialog}
              />
              {leaderboard ? <NetworkLeaderboardCard leaderboard={leaderboard} /> : null}
            </Stack>
          </Box>

          <Stack spacing={1.2} sx={{ minWidth: 0, maxWidth: 276 }}>
            <NetworkSummaryCard rows={model.glanceRows} />
            <NetworkInviteCard onInvite={model.openInviteDialog} />
          </Stack>
        </Box>
      )}

      {model.error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {model.error}
        </Alert>
      ) : null}

      <InviteDialog open={model.inviteOpen} onClose={model.closeInviteDialog} />

      <Snackbar
        open={Boolean(model.notice?.message)}
        autoHideDuration={2800}
        onClose={() => model.setNotice({ message: "", severity: "info" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={model.notice?.severity || "info"}
          onClose={() => model.setNotice({ message: "", severity: "info" })}
          sx={{ width: "100%" }}
        >
          {model.notice?.message || ""}
        </Alert>
      </Snackbar>
    </>
  );
}
